import { Play, Square } from 'lucide-solid';
import { createEffect, createMemo, createSignal, Show, untrack, type JSX } from 'solid-js';
import { EffectUnavailable } from '../components/ui';
import { EffectSyncControl, createEffectSyncModel } from '../components/EffectSyncControl';
import type { EffectLayout } from '../lib/effectLayout';
import {
  effectPackageDefaults,
  scalarControlValues,
  effectPackageFromLiveEffect,
  effectPackageFromMirror,
  MUSIC_RATE_PARAMETER,
  operatorUnavailableReason,
  type EffectPackageItem,
  type EffectPackageControlValue,
  type EffectPackageParameterValue,
} from '../lib/effectPackages';
import { effectRoomPathParams, type SchematicLayout } from '../lib/panelElements';
import { reportError } from '../lib/plainErrors';
import { useVenue } from '../state/VenueProvider';
import { createSettingHold, effectSettingsFrom, recordEffectSettings, rememberedSettings, sendSettingGesture } from '../state/effectSettings';
import { EffectPackageControls } from './EffectPackageControls';
import { EffectPresets } from './EffectPresets';
import { TEST_IDS, testIdFor } from '../testIds';

interface EffectPackagePanelProps {
  /** The package's own card, straight from its signed manifest. Re-read whenever the version changes. */
  item: EffectPackageItem;
  /** The room's effect geometry, which every package start carries. */
  layout?: { layout: EffectLayout; elementCount?: number };
  /** The rig author's room walk, sent so a path-travelling effect takes the authored route. */
  roomPath?: SchematicLayout;
  /** A read of the room's geometry is in flight. Say nothing and hold Start disabled. */
  loading: boolean;
  /** Why the effect cannot run here, in our words. Staff-only, behind "Details". */
  unavailableDetail: string;
  /** An operator double-clicked this effect's card and is asking for Play without scrolling to it. */
  startRequested?: () => boolean;
  /** Clear that request. Called before the start runs, so a re-opened card never replays an old one. */
  onStartHandled?: () => void;
}

/**
 * ONE panel, generated from whatever the package's manifest declares.
 *
 * There is no table of effects here and no branch on which effect is open: numbers become sliders,
 * `minX`/`maxX` pairs become one range control, enums become a segmented control and booleans become a
 * switch, all from the same signed document the controller validates against. An effect added to the
 * library is a card and a working panel with no console release.
 */
export function EffectPackagePanel(props: EffectPackagePanelProps): JSX.Element {
  const venue = useVenue();
  const [fieldError, setFieldError] = createSignal<{ key: string; message: string }>();
  const prefix = createMemo(() => testIdFor.packagePrefix(props.item.packageId));
  const readout = createMemo(() => {
    const current = effectPackageFromMirror(venue.mirror()?.effect)
      ?? (venue.controllerState()?.effects ?? []).map(effectPackageFromLiveEffect).find(Boolean);
    return current?.packageId === props.item.packageId ? current : undefined;
  });
  const effectId = createMemo(() => readout()?.effectId ?? '');
  const runningSettings = createMemo(() => (venue.controllerState()?.effects ?? [])
    .map((entry) => ({ entry, settings: effectSettingsFrom(entry) }))
    .find(({ settings }) => settings?.packageId === props.item.packageId));
  const roomSettings = createMemo(() => {
    const saved = venue.microRecordWrites() ? venue.effectRecord?.(props.item.packageId) : undefined;
    return saved ? recordEffectSettings(saved.record.state, props.item.packageId)
      : runningSettings()?.settings ?? rememberedSettings(venue.roomDoc(), props.item.packageId);
  });
  const hold = createSettingHold(roomSettings, () => String(runningSettings()?.entry.id ?? 'stopped'));
  const values = createMemo<Record<string, EffectPackageControlValue>>(() => ({
    ...effectPackageDefaults(props.item),
    ...hold.shown().parameters as Record<string, EffectPackageControlValue>,
  }));

  const sync = createEffectSyncModel({
    effectId,
    reported: () => {
      const settings = hold.shown().sync;
      const clock = readout()?.sync;
      return settings ? {
        on: settings.enabled, musicRate: settings.musicRate,
        mode: clock?.mode ?? '', bpm: clock?.bpm ?? null, locked: clock?.locked ?? false,
      } : undefined;
    },
    remembered: () => {
      const settings = hold.shown().sync;
      return settings ? { on: settings.enabled, musicRate: settings.musicRate } : undefined;
    },
    onChange: (field, value) => {
      sendSettingGesture(venue, props.item.packageId, [{ path: `sync/${field}`, value }], true,
        hold, (error) => {
          reportError('Sync to music', error, "The controller couldn't change music sync.");
        }, () => setFieldError(undefined));
    },
  });

  function update(
    parameters: Record<string, EffectPackageControlValue>,
    _gestureKey: string,
    final: boolean,
  ): void {
    setFieldError(undefined);
    // Only controls the installed package declares. A look saved against an older version may name one it
    // has since dropped, and the controller refuses a command naming an undeclared control whole, which
    // would lose every other control of the look with it.
    const declared = Object.entries(parameters).filter(([key]) => Object.hasOwn(props.item.parameters, key));
    const writes = declared.map(([key, value]) => ({ path: `parameters/${key}`, value }));
    sendSettingGesture(venue, props.item.packageId, writes, final, hold, (error) => {
      const plain = reportError('effect parameter', error,
        "The controller refused that setting. The controls now show the room's values.");
      const key = declared[0]?.[0] ?? '';
      setFieldError({ key, message: plain.message || props.item.parameters[key]?.validate || 'The controller refused that setting.' });
    }, () => setFieldError(undefined));
  }

  /** What a saved look records for a package: its knob positions, which are all a package declares. */
  const savedValues = createMemo<Record<string, EffectPackageParameterValue>>(() => scalarControlValues(values()));

  async function start(): Promise<void> {
    const loaded = props.layout;
    if (!loaded) return;
    if (!await venue.confirmTakeover({
      source: 'effect',
      action: `starts ${props.item.name}`,
    })) return;
    await venue.runAction('start-effect-package', async () => {
      const started = await venue.controllerCall<{ effectId?: string }>('start_effect_package', {
        packageId: props.item.packageId,
        layout: loaded.layout,
        ...effectRoomPathParams(props.roomPath),
      });
      return started;
    }, `${props.item.name} started`);
  }

  // A double-click on this effect's card is this Play button, pressed. Same command, same remembered
  // settings, same takeover prompt — there is one start, and the card reaches it.
  createEffect(() => {
    if (!props.startRequested?.()) return;
    props.onStartHandled?.();
    untrack(() => void start());
  });

  /**
   * Put every control back to what the manifest starts it at, and tell the room so.
   *
   * This is the write the panel used to make by ACCIDENT on every Start — the full default bag, over
   * whatever the room was set to. Asked for and named, it is the one action that should do that. The
   * rate is not in it: it belongs to the shared sync surface, not to this card's controls.
   *
   * Running and stopped both write the room's remembered settings immediately.
   */
  function resetToDefaults(): void {
    const defaults = effectPackageDefaults(props.item);
    delete defaults[MUSIC_RATE_PARAMETER];
    update(defaults, 'reset', true);
  }

  async function stop(): Promise<void> {
    const id = effectId();
    if (!id) return;
    await venue.runAction(
      'stop-effect-package',
      () => venue.controllerCall('stop_effect', { effectId: id }),
      'Effect stopped',
    );
  }

  const available = (): boolean => Boolean(props.layout) && props.item.available;
  const settingsEnabled = (): boolean => venue.controlAvailable();

  return (
    <section
      class="surface effect-stage effect-package-panel"
      data-testid={TEST_IDS.effectPackagePanel}
      data-package-id={props.item.packageId}
      data-package-version={props.item.version}
      data-effect-running={readout()?.running ? 'true' : 'false'}
    >
      <div class="section-heading">
        <div>
          <h2>{props.item.name}</h2>
        </div>
        <button
          type="button"
          class="button button-secondary"
          disabled={!settingsEnabled() || venue.actionBusy() === 'reset-effect-package'}
          onClick={resetToDefaults}
          data-testid={TEST_IDS.resetEffectPackage}
        >Reset to defaults</button>
      </div>

      <Show when={props.item.audio !== 'none' && props.item.parameters[MUSIC_RATE_PARAMETER] !== undefined
        && (!venue.recordRigSelected() || venue.piLineSelected())}>
        <EffectSyncControl model={sync} description={props.item.sync.description ?? ''} idPrefix={prefix()} />
      </Show>

      <EffectPackageControls
        item={props.item}
        values={values}
        synced={sync.enabled}
        disabled={() => !settingsEnabled()}
        idPrefix={prefix()}
        fixtures={() => venue.rig()?.fixtures ?? []}
        fieldError={fieldError}
        committing={(key) => hold.committing(`parameters/${key}`)}
        onUpdate={update}
      />

      {/* A preset lands through the same setting sender as a slider. */}
      <EffectPresets
        packageId={props.item.packageId}
        idPrefix={prefix()}
        values={savedValues}
        onApply={(preset) => update(preset.values, 'preset', true)}
      />

      <Show
        when={available() || props.loading}
        fallback={(
          <EffectUnavailable
            effectName={props.item.name}
            reason={operatorUnavailableReason(props.item)}
            detail={props.item.unavailableReason || props.unavailableDetail}
            testId={TEST_IDS.effectPackageUnavailable}
          />
        )}
      >
        <Show
          when={effectId()}
          fallback={(
            <button
              class="button button-primary button-large"
              disabled={!available() || !venue.controlsEnabled() || venue.actionBusy() === 'start-effect-package'}
              onClick={() => void start()}
              data-testid={TEST_IDS.startEffectPackage}
            ><Play size={18} /> Start</button>
          )}
        >
          <button
            class="button button-danger button-large"
            disabled={!venue.controlsEnabled() || venue.actionBusy() === 'stop-effect-package'}
            onClick={() => void stop()}
            data-testid={TEST_IDS.stopEffectPackage}
          ><Square size={17} /> Stop</button>
        </Show>
      </Show>
    </section>
  );
}
